import React from 'react';
import {
  Box, Drawer, Typography, IconButton, Divider, Chip, Button, Grid, Paper, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { ModuleField } from '../../types';
import { brandColors, RADIUS, brandCss, BASE_FONT_SIZE, APP_FONT_FAMILY } from '../../theme/appTheme';
import { useLocale } from '../../contexts/LocaleContext';
import {
  formatFieldDisplay, getRecordTitle, resolveFieldValue,
} from '../../utils/recordDisplayUtils';
import { isFieldVisible } from '../../utils/fieldVisibility';

interface RecordDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  row: Record<string, unknown> | null;
  fields: ModuleField[];
  moduleTitle: string;
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  footer?: React.ReactNode;
}

const statusColors: Record<string, { bg: string; color: string; label?: string }> = {
  active: { bg: '#dcfce7', color: '#166534', label: 'Active' },
  inactive: { bg: '#fee2e2', color: '#991b1b', label: 'Inactive' },
  approved: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
  completed: { bg: '#dbeafe', color: '#1d4ed8', label: 'Completed' },
  in_progress: { bg: '#fef3c7', color: '#b45309', label: 'Ongoing' },
  assigned: { bg: '#e0e7ff', color: '#4338ca', label: 'Assigned' },
  created: { bg: '#f1f5f9', color: '#475569', label: 'New' },
  pending: { bg: '#f1f5f9', color: '#475569', label: 'New' },
  paid: { bg: '#dcfce7', color: '#166534', label: 'Paid' },
  cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
  maintenance: { bg: '#fef3c7', color: '#b45309', label: 'In service' },
  owned: { bg: '#e0e7ff', color: '#3730a3', label: 'Owned (Company)' },
  leased: { bg: '#fef3c7', color: '#b45309', label: 'Vendor leased' },
  rented: { bg: '#fef3c7', color: '#b45309', label: 'Vendor leased' },
};

const friendlyStatus = (status: string) => {
  const sc = statusColors[status];
  if (sc?.label) return sc.label;
  return status.replace(/_/g, ' ');
};

const isImageUrl = (url: string) => /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url);

const toFormData = (row: Record<string, unknown>, fields: ModuleField[]): Record<string, string> => {
  const data: Record<string, string> = {};
  fields.forEach((f) => {
    data[f.name] = String(row[f.name] ?? '');
  });
  if (data.ownerType === 'rented') data.ownerType = 'leased';
  return data;
};

export const RecordDetailDrawer: React.FC<RecordDetailDrawerProps> = ({
  open, onClose, row, fields, moduleTitle, canEdit, canDelete, onEdit, onDelete, footer,
}) => {
  const { formatDate, formatCurrency } = useLocale();
  if (!row) return null;

  const title = getRecordTitle(row, fields, moduleTitle);
  const status = String(row.status || row.paymentStatus || '').toLowerCase();
  const sc = statusColors[status] || statusColors.active;
  const formatters = { formatDate, formatCurrency };
  const formLike = toFormData(row, fields);
  const visible = fields.filter((f) => isFieldVisible(f, formLike));

  const groups: { title: string; fields: ModuleField[] }[] = [];
  visible.forEach((field) => {
    const section = field.section
      || (field.name === 'status' ? 'Active or Inactive' : null)
      || (field.type === 'image' || field.type === 'file' ? 'Documents & Photos' : null)
      || 'Full details';
    const last = groups[groups.length - 1];
    if (last && last.title === section) last.fields.push(field);
    else groups.push({ title: section, fields: [field] });
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 440, md: 520 },
          borderTopLeftRadius: { sm: 16 },
          borderBottomLeftRadius: { sm: 16 },
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: APP_FONT_FAMILY }}>
        <Box sx={{
          px: { xs: 2.5, sm: 3 },
          pt: 2.5,
          pb: 2.75,
          background: brandCss.formHeaderGradient,
          color: brandCss.formHeaderText,
          position: 'relative',
        }}>
          <Box sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(ellipse 60% 80% at 100% 0%, rgba(255,255,255,0.18), transparent 55%)',
          }} />
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, opacity: 0.85, mb: 0.5 }}>
                {moduleTitle}
              </Typography>
              <Typography fontWeight={800} fontSize={{ xs: 20, sm: 22 }} sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}>
                {title}
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {status && (
            <Chip
              label={friendlyStatus(status)}
              size="small"
              sx={{
                mt: 1.75,
                bgcolor: sc.bg,
                color: sc.color,
                fontWeight: 700,
                fontSize: BASE_FONT_SIZE,
                height: 28,
                textTransform: 'none',
              }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 2.5 }, py: 2.5, bgcolor: '#f8fafc' }}>
          {groups.map((group) => (
            <Box key={group.title} sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 700, color: brandCss.primaryDark, mb: 1.25 }}>
                {group.title}
              </Typography>
              <Grid container spacing={1.5}>
                {group.fields.map((field) => {
                  const raw = resolveFieldValue(row, field);
                  if (field.type === 'image' || field.type === 'file') {
                    if (!raw) return null;
                    const url = String(raw);
                    return (
                      <Grid item xs={12} key={field.name}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}`, bgcolor: '#fff' }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: brandColors.textMuted, mb: 0.75 }}>{field.label}</Typography>
                          {isImageUrl(url) ? (
                            <Box component="img" src={url} alt={field.label}
                              sx={{ maxWidth: '100%', maxHeight: 140, borderRadius: 1.5, border: `1px solid ${brandColors.border}` }} />
                          ) : (
                            <Link href={url} target="_blank" rel="noopener noreferrer"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 700, fontSize: BASE_FONT_SIZE }}>
                              <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
                              View document
                            </Link>
                          )}
                        </Paper>
                      </Grid>
                    );
                  }
                  const display = formatFieldDisplay(raw, field, formatters);
                  return (
                    <Grid item xs={12} sm={field.type === 'textarea' ? 12 : 6} key={field.name}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          height: '100%',
                          borderRadius: `${RADIUS}px`,
                          border: `1px solid ${brandColors.border}`,
                          bgcolor: '#fff',
                        }}
                      >
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: brandColors.textMuted, mb: 0.5 }}>
                          {field.label}
                        </Typography>
                        <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 600, color: brandColors.textDark, wordBreak: 'break-word', lineHeight: 1.4 }}>
                          {display}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}

          {(row.createdAt || row.updatedAt) ? (
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Grid container spacing={1.5}>
                {row.createdAt ? (
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 11, color: brandColors.textMuted }}>Created</Typography>
                    <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 600 }}>
                      {formatDate(String(row.createdAt), { dateStyle: 'medium', timeStyle: 'short' })}
                    </Typography>
                  </Grid>
                ) : null}
                {row.updatedAt ? (
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 11, color: brandColors.textMuted }}>Updated</Typography>
                    <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 600 }}>
                      {formatDate(String(row.updatedAt), { dateStyle: 'medium', timeStyle: 'short' })}
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>
            </Box>
          ) : null}

          {footer}
        </Box>

        {(canEdit || canDelete) && (
          <Box sx={{
            px: 2.5, py: 1.75,
            borderTop: `1px solid ${brandColors.border}`,
            display: 'flex', gap: 1, bgcolor: '#fff', flexWrap: 'wrap',
          }}>
            {canEdit && onEdit && (
              <Button fullWidth variant="contained" startIcon={<EditOutlinedIcon />} onClick={onEdit}
                sx={{
                  textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px`,
                  background: brandCss.gradientPrimary, boxShadow: brandCss.shadowButton,
                }}>
                Edit
              </Button>
            )}
            {canDelete && onDelete && (
              <Button fullWidth variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={onDelete}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px` }}>
                Delete
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
